import axios from "axios";
import store from "@/store";
import {Mutations} from "@/store/enums/StoreEnums";
import AvatarApiConfig from "@/core/config/AvatarApiConfig";
import JwtService from "@/core/services/JwtService";

// The picture of the TV user as the new API gives it (always a ready address for an <img>, for both upload types).
// Until it is loaded the old value of the login data stays; after it, every place that reads
// currentUser.settings.settings.avatar gets this address (the login data of the old API would bring the old one back).
const known = {loaded: false, url: null as string | null};

// the api key of the user (given at the login) is the Bearer token of the endpoint
const headers = () => ({Authorization: `Bearer ${JwtService.getToken()}`});

// Puts the address of the picture into a login data object (used when the store takes a user)
export const withAvatar = (data: any) => {
    if (known.loaded && data && data.settings && data.settings.settings) {
        data.settings.settings.avatar = known.url;
    }
    return data;
};

// The server builds the address from its own APP_URL, which can be http:// while the cabinet is on https://: the browser
// blocks such a picture (mixed content). On an https page the same address is asked for over https.
const secure = (url: string | null): string | null =>
    url && window.location.protocol === "https:" ? url.replace(/^http:\/\//i, "https://") : url;

// the new address goes to the store: SET_USER applies it
const publish = (url: string | null) => {
    known.loaded = true;
    known.url = secure(url);
    const current = store.getters.currentUser;
    if (current) {
        store.commit(Mutations.SET_USER, current);
    }
};

// a user leaves: the next one must not get the picture of the first
export const resetAvatar = () => {
    known.loaded = false;
    known.url = null;
};

export const loadAvatar = async () => {
    if (!JwtService.getToken()) return;
    try {
        const response = await axios.get(AvatarApiConfig.AVATAR, {headers: headers()});
        publish((response.data && response.data.avatar_url) || null);
    } catch (e) {
        // the old value stays
    }
};

export type AvatarResult = {success: boolean; message?: string};

// the message of a failed answer: the first error of a 422 or the text of the server
const failure = (e: any): AvatarResult => {
    const data = e && e.response && e.response.data;
    const errors = data && data.errors;
    const first = errors && Object.values(errors)[0];
    const message = (Array.isArray(first) ? first[0] : first) || (data && (data.message || data.error_code)) || "";
    return {success: false, message: String(message)};
};

export const uploadAvatar = async (file: File): Promise<AvatarResult> => {
    const form = new FormData();
    form.append("avatar", file);
    try {
        const response = await axios.post(AvatarApiConfig.AVATAR, form, {headers: headers()});
        publish((response.data && response.data.avatar_url) || null);
        return {success: true};
    } catch (e) {
        return failure(e);
    }
};

export const removeAvatar = async (): Promise<AvatarResult> => {
    try {
        await axios.delete(AvatarApiConfig.AVATAR, {headers: headers()});
        publish(null);
        return {success: true};
    } catch (e) {
        return failure(e);
    }
};
